import axios from "axios";
import store from "@/store";
import TimeService from "@/helpers/TimeService";
import AlertService from "@/helpers/AlertService";
import RouterService from "@/helpers/RouterService";

/**
 * - Description -
 * APIService contains all the methods that allows the webapp to communicate with the backend services.
 * It provides also some facilities to intercept the errors and to enrich the headers with the user
 * token.
 *
 * - Usage -
 * Using APIService
 *
 * import APIService from '@/helpers/APIService.js';
 * APIService.execute(requestUrl, requestBody)
 *    .then ( response => { this.yourDataSuccess = response } );
 *    .catch ( error => { this.yourDataError = error } )
 */
export default {
    name: "APIService",
    call: callService,
    login: executeLogin,
    execute: executeCall,
    download: executeDownload,
    upload: executeUpload,
    bulk: executeUploadBulk,
    superadmin: executeSuperAdmin,
    radar: executeRadar
};

// Progressive Number for identify functions called by the UI.
let progressiveNum = 0;
/**
 * Axios Interceptor
 * Used to intercept errors and handle them in any case.
 */
axios.interceptors.response.use(
    function (response) {
        return response;
    },
    function (error) {
        // handle error
        console.log("***ERROR", error);
        if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            if (error.response.status === 401) {
                // Authorization error - Handling case when Axios do not intercept the code.
                let userInfo = store.getters["UserState/getUserInfo"];
                if (userInfo.isLogged) {
                    // TODO implement token refresh - for now set isLogged=false and go to Login
                    console.debug("API.SERVICE - Error: Authentication Failed - Expired Token - TODO token refresh");
                    logout();
                } else {
                    console.debug("API.SERVICE - Error: Authentication Failed");
                }
            } else {
                console.debug("API.SERVICE - Error status: ", error.response.status);
                // TODO implement retry
            }
        } else if (error.request) {
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js
            console.debug("API.SERVICE - Error request: ", error.request);
            logout();
        } else {
            // Something happened in setting up the request that triggered an Error
            console.debug("API.SERVICE - Error message: ", error.message);
            if (error.message.includes("timeout")) {
                // Axios Timeout - create alert
                let alertOptions = {
                    type: "error",
                    message: "Network error"
                };
                AlertService.createAlert(alertOptions);
            } else {
                let userInfo = store.getters["UserState/getUserInfo"];
                if (userInfo.isLogged) {
                    // TODO implement token refresh - for now set isLogged=false and go to Login
                    console.debug("API.SERVICE - Error: Authentication Failed - Expired Token - TODO token refresh");
                    logout();
                } else {
                    console.debug("API.SERVICE - Error: Authentication Failed");
                }
            }

        }
        throw error;
    }
);


function logout() {
    let userInfo = store.getters["UserState/getUserInfo"];
    userInfo.isLogged = false;
    store.dispatch("UserState/setUserInfo", userInfo);
    RouterService.goTo('/homepage');
    let alertOptions = {
        type: "error",
        message: "Please login again"
    };
    AlertService.createAlert(alertOptions);
}

function buildRequestBody(data) {
    // Create request Body
    let userInfo = store.getters["UserState/getUserInfo"];
    let userOptions = store.getters["UserState/getUserOptions"];
    let requestBody = {
        contextId: userOptions.contextId,
        scenarioId: userInfo.scenarioId,
        requestInteraction: data.interactionData,
        additionalElement: data.additionalData,
        // parametri opzionali
        ...(data.page !== undefined && { page: data.page }),
        ...(data.size !== undefined && { size: data.size }),
        ...(data.approved !== undefined && { approved: data.approved }),
        ...(data.name !== undefined && { name: data.name }),
    };
    return requestBody;
}


function callService(type, requestUrl, data) {
    let requestBody = data.data;
    let method = data.method;
    if (type === "normal") {
        return executeCall(requestUrl, requestBody);
    }else if (type === "bulk"){
        let requestBody = data.data;
        let file = data.file;
        return executeUploadBulk(requestUrl, requestBody, file);
    } else if (type === "upload") {
        let requestBody = data.data;
        let file = data.file;
        return executeUpload(requestUrl, requestBody, file);
    } else if (type === "download") {
        return executeDownload(requestUrl, requestBody);
    } else if (type === "login") {
        return executeLogin(requestUrl, requestBody);
    } else if (type === "superAdmin") {
        return executeSuperAdmin(requestUrl, requestBody, method);
    } else if (type === "public") {
        return executePublic(requestUrl, requestBody);
    }else if (type === "radar") {
        return executeRadar(requestUrl, requestBody,method);
    }
    throw new Error("API.SERVICE - Request type is not valid.");
}


function execute(options, urlsInfo, data, method) {
    let progressiveNumber = progressiveNum;
    TimeService.startTiming(urlsInfo.url + "__" + progressiveNumber);
    progressiveNum = progressiveNum + 1;
    return axios({
        method: method || "post",
        headers: options.headers,
        responseType: options.responseType,
        url: urlsInfo.url,
        data: data,
        timeout: urlsInfo.timeout || 0 //default = 0 (NO timeout)
    })
        .then(function (response) {
            // handle success
            console.debug("API.SERVICE - Response for url <" + urlsInfo.url + "> :", response);
            TimeService.endTiming(urlsInfo.url + "__" + progressiveNumber);
            return response.data;
        })
        .catch(function (error) {
            // handle error
            console.debug("API.SERVICE - Error for url <" + urlsInfo.url + "> :", error);
            TimeService.endTiming(urlsInfo.url + "__" + progressiveNumber);
            throw error;
        });
}

/**
 * Login post
 * @param requestUrl the url of the login
 * @param data username and password
 * @returns {*} the response of the backend
 */
function executeLogin(urlsInfo, data) {
    let options = {
        headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
        },
        responseType: "json",
    };
    return execute(options, urlsInfo, data);
}

/**
 * Generic backend service handler.
 * @param requestUrl the url to be called
 * @param data the payload data, divided into interactionData and additionalElement
 * @returns {*} the response of the backend
 */
function executeCall(urlsInfo, data) {
    let userInfo = store.getters["UserState/getUserInfo"];
    let requestBody = buildRequestBody(data);
    console.debug("API.SERVICE - Request for url <" + urlsInfo.url + "> :", requestBody);
    let options = {
        headers: {
            "Authorization": "Bearer " + userInfo.token,
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
        },
        responseType: "json",
    };
    return execute(options, urlsInfo, requestBody);
}

function executeSuperAdmin(urlsInfo, data, method) {
    let userInfo = store.getters["UserState/getUserInfo"];
    let requestBody = data;
    console.debug("API.SERVICE - Request for url <" + urlsInfo.url + "> :", requestBody);
    let options = {
        headers: {
            "Authorization": "Bearer " + userInfo.token,
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
        },
        responseType: "json",
    };
    return execute(options, urlsInfo, requestBody, method);
}
function executeRadar(urlsInfo, data, method) {
    let userInfo = store.getters["UserState/getUserInfo"];
    let requestBody = data;
    console.debug("API.SERVICE - Request for url <" + urlsInfo.url + "> :", requestBody);
    let options = {
        headers: {
            "Authorization": "Bearer " + userInfo.token,
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
        },
        responseType: "json",
    };
    return execute(options, urlsInfo, requestBody, method);
}

function executePublic(urlsInfo, data) {
    let requestBody = data;
    console.debug("API.SERVICE - Request for url <" + urlsInfo.url + "> :", requestBody);
    let options = {
        headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
        },
        responseType: "json",
    };
    return execute(options, urlsInfo, requestBody);
}


function executeDownload(urlsInfo, data) {
    let userInfo = store.getters["UserState/getUserInfo"];
    let requestBody = buildRequestBody(data);
    let options = {
        headers: {
            "Authorization": "Bearer " + userInfo.token,
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
        },
        responseType: "blob",
    };
    return execute(options, urlsInfo, requestBody);
}


/**
 * Generic upload service handler.
 * @param requestUrl the url to be called
 * @param data the payload data, divided into interactionData and additionalElement
 * @param file the file to be uploaded
 * @returns {*} the response of the backend
 */
function executeUpload(urlsInfo, data, file) {
    let userInfo = store.getters["UserState/getUserInfo"];
    let requestBody = new FormData();
    requestBody.append("file", file);
    requestBody.append("request", JSON.stringify(buildRequestBody(data)));
    let options = {
        headers: {
            "Authorization": "Bearer " + userInfo.token,
            "Content-Type": "multipart/form-data",
            "Access-Control-Allow-Origin": "*",
        },
        responseType: "json",
    };
    return execute(options, urlsInfo, requestBody);
}

function executeUploadBulk(urlsInfo, data, file) {
    let userInfo = store.getters["UserState/getUserInfo"];
    let requestBody = new FormData();
    for (let i = 0; i < file.length; i++) {
        requestBody.append("file", file[i].data);
    }
    requestBody.append("request", JSON.stringify(buildRequestBody(data)));

    let options = {
        headers: {
            "Authorization": "Bearer " + userInfo.token,
            "Content-Type": "multipart/form-data",
            "Access-Control-Allow-Origin": "*",
        },
        responseType: "json",
    };
    return execute(options, urlsInfo, requestBody);
}



